<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Modal from '@/components/ui/Modal.vue'
import { api, extractError } from '@/api/client'
import type { SwitchQuotaHistoryItem, SwitchQuotaHistorySummary } from '@/types'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

const loading = ref(false)
const error = ref('')
const summary = ref<SwitchQuotaHistorySummary | null>(null)
const items = ref<SwitchQuotaHistoryItem[]>([])
const showAccruals = ref(true)
const showRecalc = ref(true)
const showDeductions = ref(true)

const filtersActive = computed(
  () => !showAccruals.value || !showRecalc.value || !showDeductions.value,
)

const filteredItems = computed(() =>
  items.value.filter((row) => {
    if (row.kind === 'accrual') return showAccruals.value
    if (row.kind === 'recalc') return showRecalc.value
    if (row.kind === 'deduction' || row.kind === 'cancelled') return showDeductions.value
    return true
  }),
)

function deltaLabel(delta: number, kind: SwitchQuotaHistoryItem['kind']): string {
  if (kind === 'cancelled') return '—'
  if (delta > 0) return `+${delta}`
  if (delta === 0) return '0'
  return String(delta)
}

function deltaClass(delta: number): string {
  if (delta > 0) return 'text-success font-semibold'
  if (delta < 0) return 'text-danger font-semibold'
  return 'text-muted'
}

function badgeClass(kind: SwitchQuotaHistoryItem['kind']): string {
  switch (kind) {
    case 'accrual':
      return 'badge badge-success'
    case 'deduction':
      return 'badge badge-danger'
    case 'recalc':
      return 'badge bg-info text-dark'
    default:
      return 'badge bg-secondary'
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get<{
      success: boolean
      summary?: SwitchQuotaHistorySummary
      items?: SwitchQuotaHistoryItem[]
      message?: string
    }>('/api/user/switch-history')
    if (!data.success) {
      error.value = data.message || 'Не удалось загрузить историю'
      return
    }
    summary.value = data.summary ?? null
    items.value = data.items ?? []
  } catch (e) {
    error.value = extractError(e)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.open,
  (open) => {
    if (open) void load()
  },
)
</script>

<template>
  <Modal :open="open" max-width="5xl" @close="emit('close')">
    <template #header>
      <div class="text-lg font-bold">История смен</div>
    </template>

    <div v-if="loading" class="py-8 text-center text-muted">Загрузка…</div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-else class="space-y-4">
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div class="rounded border border-white-light bg-black/[0.02] p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted">Доступно сейчас</div>
          <div class="mt-1 text-2xl font-extrabold text-primary">{{ summary?.available_now ?? '—' }}</div>
          <div class="mt-1 text-xs text-muted">
            Пересчитывается от оставшегося срока ключей (1 смена / 7 дн., максимум 4 на ключ)
          </div>
        </div>
        <div class="rounded border border-white-light bg-black/[0.02] p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted">Начислено за ключи</div>
          <div class="mt-1 text-2xl font-extrabold">{{ summary?.accrued_total ?? '—' }}</div>
        </div>
        <div class="rounded border border-white-light bg-black/[0.02] p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted">От администратора</div>
          <div class="mt-1 text-2xl font-extrabold text-success">{{ summary?.accrued_admin_total ?? 0 }}</div>
        </div>
        <div class="rounded border border-white-light bg-black/[0.02] p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted">Потрачено на смены</div>
          <div class="mt-1 text-2xl font-extrabold text-danger">{{ summary?.spent_total ?? '—' }}</div>
        </div>
      </div>

      <div>
        <div class="mb-2 font-bold">Хронология смен</div>
        <p class="mb-3 text-xs leading-relaxed text-muted">
          Колонка «Осталось» — running balance: каждая строка = баланс после события.
          Начисление +N, пересчёт задаёт абсолютное значение по сроку ключей, списание −1 после подтверждения смены.
        </p>
        <div class="mb-3 flex flex-wrap gap-4 text-sm">
          <label class="flex cursor-pointer items-center gap-2">
            <input v-model="showAccruals" type="checkbox" class="h-4 w-4 rounded border-white-light text-primary" />
            Начисления
          </label>
          <label class="flex cursor-pointer items-center gap-2">
            <input v-model="showRecalc" type="checkbox" class="h-4 w-4 rounded border-white-light text-primary" />
            Пересчёт
          </label>
          <label class="flex cursor-pointer items-center gap-2">
            <input v-model="showDeductions" type="checkbox" class="h-4 w-4 rounded border-white-light text-primary" />
            Списания
          </label>
        </div>
        <p v-if="filtersActive" class="mb-3 text-xs text-warning">
          При фильтрах «Осталось» может выглядеть с разрывами — включите все типы для непрерывной цепочки.
        </p>

        <div v-if="!filteredItems.length" class="rounded border border-white-light p-4 text-sm text-muted">
          Нет записей по выбранным фильтрам.
        </div>
        <div v-else class="overflow-x-auto rounded border border-white-light">
          <table class="w-full min-w-[760px] text-sm">
            <thead class="border-b border-white-light bg-panel-header text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th class="px-3 py-2">Когда</th>
                <th class="px-3 py-2">Событие</th>
                <th class="px-3 py-2">Детали</th>
                <th class="px-3 py-2 text-right">Смены</th>
                <th class="px-3 py-2 text-right">Осталось</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in filteredItems"
                :key="`${row.at}-${idx}`"
                class="border-b border-white-light/60 last:border-0"
              >
                <td class="px-3 py-2 whitespace-nowrap">{{ row.at }}</td>
                <td class="px-3 py-2">
                  <span :class="badgeClass(row.kind)">{{ row.event_label }}</span>
                </td>
                <td class="px-3 py-2">
                  <div>{{ row.details }}</div>
                  <div v-if="row.sub_details" class="text-xs text-muted">{{ row.sub_details }}</div>
                </td>
                <td class="px-3 py-2 text-right" :class="deltaClass(row.delta)">
                  {{ deltaLabel(row.delta, row.kind) }}
                </td>
                <td class="px-3 py-2 text-right font-semibold">{{ row.balance }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Modal>
</template>
